import { dateKeyToCalendarDate, toDateKey } from '@apps/web/lib/date_utils';
import { type OrganizationConversationAuditEvent } from '@apps/web/swr_data_fetching/conversation_ownership';
import {
    type ApiResponseType,
    type Message,
} from '@libraries/typescript/api_client';

export type ConversationCall = NonNullable<
    ApiResponseType<
        '/organizations/{organization_id}/conversations/{conversation_id}',
        'get'
    >['calls']
>[number];

export type ConversationLedgerEvent = NonNullable<
    ApiResponseType<
        '/organizations/{organization_id}/conversations/{conversation_id}',
        'get'
    >['ledger_events']
>[number];

export type ConversationTimelineEntry =
    | {
          readonly type: 'message';
          readonly id: string;
          readonly timestamp: string;
          readonly message: Message;
      }
    | {
          readonly type: 'call';
          readonly id: string;
          readonly timestamp: string;
          readonly call: ConversationCall;
      }
    | {
          readonly type: 'ledger_event';
          readonly id: string;
          readonly timestamp: string;
          readonly ledgerEvent: ConversationLedgerEvent;
      }
    | {
          readonly type: 'audit';
          readonly id: string;
          readonly timestamp: string;
          readonly auditEvent: OrganizationConversationAuditEvent;
      };

export function getConversationTimelineTimestamp(
    entry: ConversationTimelineEntry,
): string {
    return entry.timestamp;
}

/**
 * Id of the newest outbound message in a timeline, which is the one that
 * carries the visible "Delivered" receipt (iMessage-style). Null when the
 * timeline has no outbound message yet.
 */
export function latestOutboundMessageId(
    entries: ReadonlyArray<ConversationTimelineEntry>,
): string | null {
    for (let index = entries.length - 1; index >= 0; index -= 1) {
        const entry = entries[index];
        if (entry?.type === 'message' && entry.message.role === 'assistant') {
            return entry.message.id;
        }
    }
    return null;
}

export function timelineDateKey(timestamp: string, timezone: string): string {
    return new Date(timestamp).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        timeZone: timezone,
    });
}

// Gap between adjacent entries that earns an inline timestamp separator,
// mirroring iMessage's periodic timestamps.
export const TIMELINE_TIMESTAMP_GAP_MS = 3 * 60 * 60 * 1000;

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Whether to show an inline timestamp separator above an entry: on the first
 * entry, after a quiet gap, or on a calendar-day change (so overnight gaps
 * under the threshold still read as "Yesterday"/"Today").
 */
export function showTimelineTimestamp(
    previousTimestamp: string | null,
    timestamp: string,
    timezone: string,
): boolean {
    if (previousTimestamp === null) {
        return true;
    }
    const gapMs =
        new Date(timestamp).getTime() - new Date(previousTimestamp).getTime();
    return (
        gapMs >= TIMELINE_TIMESTAMP_GAP_MS ||
        timelineDateKey(previousTimestamp, timezone) !==
            timelineDateKey(timestamp, timezone)
    );
}

export type TimelineTimestampLabel = {
    readonly dayLabel: string;
    readonly timeLabel: string;
};

/**
 * iMessage-style separator label in the org timezone, split so the day part
 * can render bolder than the time: "Today" / "Yesterday" / a weekday within
 * the last week / "Mon, Aug 4" (with year once it differs from the current).
 */
export function formatTimelineTimestamp(
    timestamp: string,
    timezone: string,
    now: Date = new Date(),
): TimelineTimestampLabel {
    const date = new Date(timestamp);
    const timeLabel = date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
        timeZone: timezone,
    });
    const dayKey = toDateKey(date, timezone);
    const nowKey = toDateKey(now, timezone);
    // Compare tz-anchored middays so the difference is whole calendar days
    // regardless of DST shifts.
    const daysAgo = Math.round(
        (dateKeyToCalendarDate(nowKey, timezone).getTime() -
            dateKeyToCalendarDate(dayKey, timezone).getTime()) /
            DAY_MS,
    );
    if (daysAgo === 0) {
        return { dayLabel: 'Today', timeLabel };
    }
    if (daysAgo === 1) {
        return { dayLabel: 'Yesterday', timeLabel };
    }
    if (daysAgo > 1 && daysAgo < 7) {
        return {
            dayLabel: date.toLocaleDateString('en-US', {
                weekday: 'long',
                timeZone: timezone,
            }),
            timeLabel,
        };
    }
    return {
        dayLabel: date.toLocaleDateString('en-US', {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
            ...(dayKey.slice(0, 4) !== nowKey.slice(0, 4) && {
                year: 'numeric',
            }),
            timeZone: timezone,
        }),
        timeLabel,
    };
}

// Number of trailing entries (entries are ordered oldest -> newest) that fall
// on the most recent calendar day in the org timezone. Falls back to at least
// `minVisible` entries so a quiet day with a single message still shows useful
// context. Never exceeds the total number of entries.
export function countRecentDayTimelineEntries(
    entries: ReadonlyArray<ConversationTimelineEntry>,
    timezone: string,
    minVisible = 5,
): number {
    if (entries.length === 0) {
        return 0;
    }
    const lastEntry = entries[entries.length - 1];
    if (!lastEntry) {
        return 0;
    }
    const lastDayKey = timelineDateKey(
        getConversationTimelineTimestamp(lastEntry),
        timezone,
    );
    let recentDayCount = 0;
    for (let index = entries.length - 1; index >= 0; index -= 1) {
        const entry = entries[index];
        if (!entry) {
            break;
        }
        const dayKey = timelineDateKey(
            getConversationTimelineTimestamp(entry),
            timezone,
        );
        if (dayKey !== lastDayKey) {
            break;
        }
        recentDayCount += 1;
    }
    return Math.min(entries.length, Math.max(recentDayCount, minVisible));
}

export function buildConversationTimelineEntries(
    messages: ReadonlyArray<Message>,
    calls: ReadonlyArray<ConversationCall>,
    ledgerEvents: ReadonlyArray<ConversationLedgerEvent> = [],
    auditEvents: ReadonlyArray<OrganizationConversationAuditEvent> = [],
): Array<ConversationTimelineEntry> {
    const messageTimestampById = new Map(
        messages.map((message) => [message.id, message.created_at]),
    );
    const entries: Array<ConversationTimelineEntry> = [
        ...messages.map((message) => ({
            type: 'message' as const,
            id: message.id,
            timestamp: message.created_at,
            message,
        })),
        ...ledgerEvents.map((event) => {
            // Anchor each ledger row just after its triggering message. A
            // late-finishing engine run carries a much newer created_at,
            // which would float the row to the bottom of the timeline and
            // break the pagination scroll restore (older pages must only
            // ever add content above the viewport).
            const messageTimestamp = messageTimestampById.get(event.message_id);
            const timestamp =
                messageTimestamp === undefined
                    ? event.created_at
                    : new Date(
                          new Date(messageTimestamp).getTime() + 1,
                      ).toISOString();
            return {
                type: 'ledger_event' as const,
                id: event.id,
                timestamp,
                ledgerEvent: event,
            };
        }),
        // One entry per call: transfers are represented on the call card
        // itself (title and destination), not as a separate timeline event.
        ...calls.map((call) => ({
            type: 'call' as const,
            id: call.id,
            timestamp: call.started_at ?? call.created_at,
            call,
        })),
        ...auditEvents.map((event) => ({
            type: 'audit' as const,
            id: event.id,
            timestamp: event.created_at,
            auditEvent: event,
        })),
    ];

    return entries.sort((a, b) => {
        const timestampDifference =
            new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime();
        if (timestampDifference !== 0) {
            return timestampDifference;
        }

        return a.id.localeCompare(b.id);
    });
}
